import React, { useEffect } from "react";
import { Box, Typography, Button, Grid, CircularProgress, Alert, TextField } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getDailyPunchByIdAction, clearGetDailyPunchByIdAction } from "../../../store/actions";

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    return `${date}  ${time}`;
  } catch {
    return iso;
  }
};

const getDistance = (record) => {
  const { totalDistanceKm, odometerStart, odometerEnd } = record;
  if (totalDistanceKm != null) return `${Number(totalDistanceKm)} km`;
  if (odometerStart != null && odometerEnd != null)
    return `${Math.max(0, Number(odometerEnd) - Number(odometerStart))} km`;
  return "-";
};

const TravelPunchingLogViewAMRM = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, successData, error, errorInfo } = useSelector(
    (state) => state.getDailyPunchByIdReducer || {}
  );
  const record = successData?.data ?? successData;

  useEffect(() => {
    dispatch(getDailyPunchByIdAction({ endPoint: `employee-daily-travel/${id}` }));
    return () => {
      dispatch(clearGetDailyPunchByIdAction());
    };
  }, [dispatch, id]);

  // ✅ FIX: Clear redux state BEFORE navigating so the list's byIdSuccess
  // useEffect doesn't see stale data and redirect to /punch
  const handleClose = () => {
    dispatch(clearGetDailyPunchByIdAction());
    navigate('/amrm/dailyActivity');
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {errorInfo || "Failed to load travel log details."}
      </Alert>
    );
  }
  if (!record) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
        <Typography color="text.secondary">No travel log found.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{
      p: 2,
      bgcolor: "background.paper",
      borderRadius: 3,
      boxShadow: 3,
      // minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      mt: { xs: 9, sm: 0 },
    }}>
      <Typography variant="h5" fontWeight={700} mb={3} >Travel Log Details</Typography>
       <Box >
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                      <Grid item size={{xs:6, sm:3}}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Travel Date"
                          value={record.travelDate || "-"}
                          InputProps={{ readOnly: true }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                        />
                      </Grid>
                      <Grid item size={{xs:6, sm:3}}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Start Time"
                          value={formatDateTime(record.startPunchTime)}
                          InputProps={{ readOnly: true }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                        />
                      </Grid>
                       <Grid item size={{xs:6, sm:3}}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Start Odometer"
                          value={record.odometerStart ?? '-'}
                          InputProps={{ readOnly: true }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                        />
                      </Grid>
                      <Grid item size={{xs:6, sm:3}}>
                        <TextField
                          size="small"
                          fullWidth
                          label="End Time"
                          value={formatDateTime(record.endPunchTime)}
                          InputProps={{ readOnly: true }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                        />
                      </Grid>
                      <Grid item size={{xs:6, sm:3}}>
                        <TextField
                          size="small"
                          fullWidth
                          label="End Odometer"
                          value={record.odometerEnd ?? '-'}
                          InputProps={{ readOnly: true }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                        />
                      </Grid>
                      <Grid item size={{xs:6, sm:3}}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Total Distance"
                          value={getDistance(record)}
                          InputProps={{ readOnly: true }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                        />
                      </Grid>
        
                </Grid>
              </Box>
            </Box>
      <Grid container spacing={2} sx={{ width: "100%",  mt: 3 }}>
             <Grid item size={{xs:12, sm:6}}>
               <Typography fontWeight={600}>Start Photo:</Typography>
               {record.startPhotoPath ? (
                 <img 
                   src={record.startPhotoPath} 
                   alt="Start Photo" 
                   style={{ maxWidth: '100%', borderRadius: 8, marginTop: 4, border: '2px solid #1976d2', boxSizing: 'border-box' }} 
                 />
               ) : (
                 <Typography color="text.secondary">No photo</Typography>
               )}
             </Grid>
             <Grid item size={{xs:12, sm:6}}>
               <Typography fontWeight={600}>End Photo:</Typography>
               {record.endPhotoPath ? (
                 <img src={record.endPhotoPath} alt="End Photo" style={{ maxWidth: '100%', borderRadius: 8, marginTop: 4,border: '2px solid #1976d2', boxSizing: 'border-box' }} />
               ) : (
                 <Typography color="text.secondary">No photo</Typography>
               )}
             </Grid>
           </Grid>
       <Box mt={3} sx={{ width: "100%",  }}>
             <Typography fontWeight={600}>In Person Photo:</Typography>
             {Array.isArray(record.inPersonPhoto) && record.inPersonPhoto.length > 0 ? (
               <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                 {record.inPersonPhoto.map((url, idx) => (
                   <img
                     key={idx}
                     src={url}
                     alt={`In Person Photo ${idx + 1}`}
                     style={{ maxWidth: 180, borderRadius: 8,border: '2px solid #1976d2', boxSizing: 'border-box' }}
                   />
                 ))}
               </Box>
             ) : (
               <Typography color="text.secondary">No photo</Typography>
             )}
           </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        {/* ✅ FIX: Use handleClose which clears Redux state before navigating */}
        <Button variant="contained" sx={{ px: 5, fontWeight: 600 }} onClick={handleClose}>
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default TravelPunchingLogViewAMRM;